import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const prepareDataForExport = (data: any[], columnVisibilityData: any[]) => {
    if (!data || data.length === 0 || !columnVisibilityData || columnVisibilityData.length === 0) {
        return { exportData: data, exportCols: columnVisibilityData };
    }

    // 1. Create a copy of columnVisibilityData and append the "Total" column
    const exportCols = [...columnVisibilityData, { columnName: "Total", keyName: "totalCount" }];

    // 2. Group data by lotNo and countNo to calculate totals
    const groupTotals: { [key: string]: number } = {};
    let grandTotal = 0;
    const isProductAssign = data.some(item => item.assignedCount !== undefined && item.assignedCount !== null);

    data.forEach((item: any) => {
        const lot = item.lotNo || "N/A";
        const count = item.countNo || "N/A";
        const key = `${lot}_${count}`;
        
        const val = isProductAssign ? (Number(item.assignedCount) || 0) : 1;
        groupTotals[key] = (groupTotals[key] || 0) + val;
        grandTotal += val;
    });

    // 3. Find the last occurrence index for each unique group key in data
    const lastIndices: { [key: string]: number } = {};
    data.forEach((item: any, index: number) => {
        const lot = item.lotNo || "N/A";
        const count = item.countNo || "N/A";
        const key = `${lot}_${count}`;
        lastIndices[key] = index;
    });

    // 4. Map the original data rows, adding the "totalCount" property
    const exportData = data.map((item: any, index: number) => {
        const lot = item.lotNo || "N/A";
        const count = item.countNo || "N/A";
        const key = `${lot}_${count}`;
        
        const totalVal = (lastIndices[key] === index) ? groupTotals[key] : "";
        return {
            ...item,
            totalCount: totalVal
        };
    });

    // 5. Append separator row
    const emptyRow: any = {};
    exportCols.forEach((col: any) => {
        emptyRow[col.keyName] = "";
    });
    exportData.push(emptyRow);

    // 6. Append GRAND TOTAL row
    const totalRow: any = {};
    exportCols.forEach((col: any) => {
        totalRow[col.keyName] = "";
    });
    
    // Set "TOTAL" in the column right before "Total" column
    const labelCol = exportCols[exportCols.length - 2];
    totalRow[labelCol.keyName] = "TOTAL";
    totalRow.totalCount = grandTotal;

    exportData.push(totalRow);

    return { exportData, exportCols };
};

export const downloadExcel = (fileName: string, data: any, columnVisibilityData: any) => {
    const { exportData, exportCols } = prepareDataForExport(data, columnVisibilityData);
    let downloadData: any = []
    exportData.forEach((item: any) => {
        const obj: any = {}
        exportCols.forEach((prop: any) => {
            obj[prop.columnName] = item[prop.keyName]
        })
        downloadData.push(obj)
    })

    const worksheet = XLSX.utils.json_to_sheet(downloadData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, fileName + ".xlsx");
};

export const printPage = (divName: string, title?: string, data?: any[], columnVisibilityData?: any[]) => {
    if (data && columnVisibilityData) {
        const { exportData, exportCols } = prepareDataForExport(data, columnVisibilityData);
        let iframe = document.getElementById('print-iframe') as HTMLIFrameElement;
        if (!iframe) {
            iframe = document.createElement('iframe') as HTMLIFrameElement;
            iframe.id = 'print-iframe';
            iframe.style.position = 'absolute';
            iframe.style.width = '0px';
            iframe.style.height = '0px';
            iframe.style.border = 'none';
            iframe.style.top = '-1000px';
            document.body.appendChild(iframe);
        }

        const iframeDoc = iframe.contentWindow?.document || iframe.contentDocument;
        if (iframeDoc) {
            let tableHeaders = exportCols.map(col => `<th>${col.columnName}</th>`).join('');
            let tableRows = exportData.map((row, idx) => {
                return `<tr>` + exportCols.map(col => `<td>${row[col.keyName] !== undefined && row[col.keyName] !== null ? row[col.keyName] : ''}</td>`).join('') + `</tr>`;
            }).join('');

            iframeDoc.open();
            iframeDoc.write(`
                <html>
                <head>
                    <title>${title || 'Print Report'}</title>
                    <style>
                        @page {
                            size: auto;
                            margin: 0mm;
                        }
                        body {
                            font-family: sans-serif;
                            padding: 20px;
                            color: #333;
                        }
                        h2 { text-align: center; text-transform: uppercase; margin-bottom: 20px; font-size: 16px; letter-spacing: 1px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
                        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; text-transform: uppercase; }
                        th { background-color: #f2f2f2; font-weight: bold; }
                        tr:nth-child(even) { background-color: #f9f9f9; }
                        @media print {
                            body {
                                padding: 15mm 10mm;
                            }
                            .no-print { display: none; }
                        }
                    </style>
                </head>
                <body>
                    <h2>${title ? title.replace(/_/g, ' ') : 'Report'}</h2>
                    <table>
                        <thead>
                            <tr>${tableHeaders}</tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>
                </body>
                </html>
            `);
            iframeDoc.close();

            setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
            }, 300);
        }
    } else {
        var printContents = (document.getElementById(divName) as HTMLInputElement).innerHTML;
        var originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
        window.location.reload();
    }
}

export const downloadFile = (fileName: string, data: any, columnVisibilityData: any) => {
    const { exportData, exportCols } = prepareDataForExport(data, columnVisibilityData);
    let downloadData: any = []
    exportData.forEach((item: any) => {
        const obj: any = {}
        exportCols.forEach((prop: any) => {
            obj[prop.columnName] = item[prop.keyName]
        })
        downloadData.push(obj)
    })

    const worksheet = XLSX.utils.json_to_sheet(downloadData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, fileName + ".csv");
}

export const convertJsonToPdf = (data: any[], columnVisibilityData: any, title: string) => {
    const { exportData, exportCols } = prepareDataForExport(data, columnVisibilityData);
    let downloadData: any = []
    exportData.forEach((item: any) => {
        const obj: any = {}
        exportCols.forEach((prop: any) => {
            obj[prop.columnName] = item[prop.keyName]
        })
        downloadData.push(obj)
    })

    const pdf = new jsPDF('p', 'mm', 'a4'); // Use standard a4 for clean reports

    const formattedTitle = title.replace(/_/g, ' ').toUpperCase();
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text(formattedTitle, pdf.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const tbody = document.createElement('tbody');

    const keys = Object.keys(downloadData[0]);

    const headerRow = document.createElement('tr');
    keys.forEach((key) => {
        const th = document.createElement('th');
        th.textContent = key.toUpperCase();
        th.style.backgroundColor = '#f8f9fa';
        th.style.border = '1px solid #ddd';
        th.style.padding = '8px';
        th.style.fontWeight = 'bold';
        th.style.fontSize = '9px';
        th.style.textAlign = 'center';
        th.style.fontFamily = 'sans-serif';
        headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);

    downloadData.forEach((item: any, index: any) => {
        const row = document.createElement('tr');
        row.style.backgroundColor = index % 2 === 0 ? '#f9f9f9' : 'white';

        keys.forEach((key) => {
            const cell = document.createElement('td');
            cell.textContent = item[key] !== undefined && item[key] !== null ? item[key].toString() : '';
            cell.style.border = '1px solid #ddd';
            cell.style.padding = '8px';
            cell.style.fontSize = '9px';
            cell.style.fontFamily = 'sans-serif';
            
            // Alignments matching normal tables
            if (key === "S.No" || key === "Total" || key === "S.No" || key === "Lot No" || key === "Count No" || key === "S.Color") {
                cell.style.textAlign = 'center';
            } else {
                cell.style.textAlign = 'left';
            }
            row.appendChild(cell);
        });

        tbody.appendChild(row);
    });

    table.appendChild(thead);
    table.appendChild(tbody);

    table.style.borderCollapse = 'collapse';
    table.style.width = '100%';
    table.style.border = '1px solid #ddd';

    document.body.appendChild(table);

    html2canvas(table).then((canvas:any) => {
        const imgData = canvas.toDataURL('image/png');

        pdf.addImage(imgData, 'PNG', 10, 25, pdf.internal.pageSize.getWidth() - 20, 0);

        pdf.save(title);

        document.body.removeChild(table);
    });
};


export const downloadPdfDocument = (rootElementId: any, downloadFileName: any) => {
    console.log(rootElementId);

    let data = document.getElementById(rootElementId) || rootElementId;
    html2canvas(data).then((canvas:any) => {
        const imgWidth = 127;
        const imgHeight = canvas.height * imgWidth / canvas.width;
        const contentDataURL = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a5');
        const position = 10;
        pdf.addImage(contentDataURL, 'PNG', 10, position, imgWidth, imgHeight);
        pdf.save(downloadFileName + '.pdf');
    });

}